<nav 
  [class]="navigationClass" 
  (mouseenter)="onMouseEnter()" 
  (mouseleave)="onMouseLeave()"
  role="navigation"
  aria-label="Main navigation"
  [style.visibility]="isNavigationReady ? 'visible' : 'hidden'">

  <!-- Header Section with Logo and Toggle -->
  <div class="acs-navigation__header">
    <div class="acs-navigation__logo" *ngIf="!collapsed || hoverExpanded">
      <img [src]="logo" [alt]="logoText + ' Logo'" class="acs-navigation__logo-img">
      <span class="acs-navigation__logo-text">{{ logoText }}</span>
    </div>
    <button 
      class="acs-navigation__toggle"
      (click)="toggleCollapse()"
      [attr.aria-label]="collapsed ? 'Expand navigation' : 'Collapse navigation'"
      [attr.aria-expanded]="!collapsed">
      <span class="acs-navigation__toggle-icon">{{ collapsed ? '▶' : '▼' }}</span>
    </button>
  </div>

  <!-- Main Navigation Menu -->
  <div class="acs-navigation__menu">
    <ul class="acs-navigation__list" role="menubar">
      <li 
        *ngFor="let item of items" 
        class="acs-navigation__item"
        [class.acs-navigation__item--active]="isItemActive(item)"
        [class.acs-navigation__item--disabled]="item.disabled"
        [class.acs-navigation__item--has-children]="item.children && item.children.length > 0"
        role="none">
        
        <!-- Main Menu Item -->
        <a 
          *ngIf="item.route && !item.external"
          [routerLink]="item.route"
          routerLinkActive="acs-navigation__link--active"
          [routerLinkActiveOptions]="{ exact: !item.children || item.children.length === 0 }"
          class="acs-navigation__link"
          [class.acs-navigation__link--disabled]="item.disabled"
          (click)="onItemClick(item, $event)"
          [attr.aria-current]="isItemActive(item) ? 'page' : null"
          [attr.aria-expanded]="item.children && item.children.length > 0 ? isExpanded(item.id) : null"
          role="menuitem"
          tabindex="0">
          
          <span class="acs-navigation__icon" [attr.aria-hidden]="true">{{ item.icon }}</span>
          <span class="acs-navigation__label" *ngIf="!collapsed || hoverExpanded">
            {{ item.label }}
            <span class="acs-navigation__badge" *ngIf="item.badge">{{ item.badge }}</span>
          </span>
          <span 
            class="acs-navigation__arrow" 
            *ngIf="(item.children && item.children.length > 0) && (!collapsed || hoverExpanded)"
            [class.acs-navigation__arrow--expanded]="isExpanded(item.id)">
            ▼
          </span>
        </a>

        <!-- External Link -->
        <a 
          *ngIf="item.route && item.external"
          [href]="item.route"
          target="_blank"
          rel="noopener noreferrer"
          class="acs-navigation__link"
          [class.acs-navigation__link--disabled]="item.disabled"
          role="menuitem"
          tabindex="0">
          
          <span class="acs-navigation__icon" [attr.aria-hidden]="true">{{ item.icon }}</span>
          <span class="acs-navigation__label" *ngIf="!collapsed || hoverExpanded">
            {{ item.label }}
            <span class="acs-navigation__external-icon">🔗</span>
            <span class="acs-navigation__badge" *ngIf="item.badge">{{ item.badge }}</span>
          </span>
        </a>

        <!-- Parent Item without Route (only expands submenu) -->
        <a
          *ngIf="!item.route && item.children && item.children.length > 0"
          class="acs-navigation__link"
          [class.acs-navigation__link--active]="isItemActive(item)"
          [class.acs-navigation__link--disabled]="item.disabled"
          (click)="onItemClick(item, $event)"
          [attr.aria-current]="isItemActive(item) ? 'page' : null"
          [attr.aria-expanded]="item.children && item.children.length > 0 ? isExpanded(item.id) : null"
          role="menuitem"
          tabindex="0">
          
          <span class="acs-navigation__icon" [attr.aria-hidden]="true">{{ item.icon }}</span>
          <span class="acs-navigation__label" *ngIf="!collapsed || hoverExpanded">
            {{ item.label }}
            <span class="acs-navigation__badge" *ngIf="item.badge">{{ item.badge }}</span>
          </span>
          <span 
            class="acs-navigation__arrow" 
            *ngIf="(!collapsed || hoverExpanded)"
            [class.acs-navigation__arrow--expanded]="isExpanded(item.id)">
            ▼
          </span>
        </a>

        <!-- Submenu -->
        <ul 
          class="acs-navigation__submenu"
          *ngIf="item.children && item.children.length > 0 && (!collapsed || hoverExpanded) && isExpanded(item.id)"
          role="menu"
          [attr.aria-labelledby]="item.id">
          
          <li 
            *ngFor="let child of item.children" 
            class="acs-navigation__subitem"
            [class.acs-navigation__subitem--active]="isItemActive(child)"
            [class.acs-navigation__subitem--disabled]="child.disabled"
            role="none">
            
            <a 
              *ngIf="child.route && !child.external"
              [routerLink]="child.route"
              routerLinkActive="acs-navigation__sublink--active"
              [routerLinkActiveOptions]="{ exact: true }"
              class="acs-navigation__sublink"
              [class.acs-navigation__sublink--disabled]="child.disabled"
              (click)="onItemClick(child, $event)"
              [attr.aria-current]="isItemActive(child) ? 'page' : null"
              role="menuitem"
              tabindex="0">
              
              <span class="acs-navigation__subicon" [attr.aria-hidden]="true">{{ child.icon }}</span>
              <span class="acs-navigation__sublabel">
                {{ child.label }}
                <span class="acs-navigation__badge" *ngIf="child.badge">{{ child.badge }}</span>
              </span>
            </a>

            <!-- External Child Link -->
            <a 
              *ngIf="child.route && child.external"
              [href]="child.route"
              target="_blank"
              rel="noopener noreferrer"
              class="acs-navigation__sublink"
              [class.acs-navigation__sublink--disabled]="child.disabled"
              role="menuitem"
              tabindex="0">
              
              <span class="acs-navigation__subicon" [attr.aria-hidden]="true">{{ child.icon }}</span>
              <span class="acs-navigation__sublabel">
                {{ child.label }}
                <span class="acs-navigation__external-icon">🔗</span>
                <span class="acs-navigation__badge" *ngIf="child.badge">{{ child.badge }}</span>
              </span>
            </a>
          </li>
        </ul>
      </li>
    </ul>
  </div>

  <!-- User Section -->
  <div class="acs-navigation__user" *ngIf="showUserSection && user">
    <div class="acs-navigation__user-info" *ngIf="!collapsed || hoverExpanded">
      <div class="acs-navigation__user-avatar">
        <div class="acs-navigation__avatar-default">
          {{ (user.FirstName || user.Name || 'U').charAt(0).toUpperCase() }}
        </div>
      </div>
      <div class="acs-navigation__user-details">
        <div class="acs-navigation__user-name">{{ user.FirstLast || user.Name || 'Unknown User' }}</div>
        <div class="acs-navigation__user-email">{{ user.Email || '' }}</div>
      </div>
    </div>
    
    <div class="acs-navigation__user-actions">
      <button 
        class="acs-navigation__user-action"
        (click)="onUserMenuAction(getUserAction())"
        [attr.aria-label]="getUserActionLabel()"
        [title]="collapsed && !hoverExpanded ? getUserActionLabel() : ''">
        {{ getUserActionIcon() }}
      </button>
    </div>
  </div>

  <!-- Mobile Overlay -->
  <div 
    class="acs-navigation__overlay" 
    *ngIf="isMobile && !collapsed"
    (click)="toggleCollapse()">
  </div>
</nav>